import { useState, useEffect } from "react";
import Required from "../../../components/common-components/Required";
import countryData from "../../../dummyData/CountryCodes.json";
import "../../../styles/screens/client-visit-screens/CreateNewVisitForm.css";
import ErrorMessageComponent from "../../../components/common-components/ErrorMessageComponent";
import BlackButton from "../../custom-components/BlackButton";
import CancelButton from "../../custom-components/CancelButton";
import { SupportTeamFormValidations } from "../../../util/Validations";
import ImagePreviewWithFilename from "../../common-components/ImagePreviewWithFilename";
import { fileDelete, fileUpload, setSelectedFile, previewImage } from "../../../slices/FileUploadSlice";
import { useDispatch } from "react-redux";
import FileUploader from "../../common-components/FileUploader";
import { SupportStringLabels } from "../../../util/Strings";
import useFileValidation from "../../../util/useFileValidation";
import { setPrePhotoFileId, setPrevPhotoFileDetails } from "../../../slices/VisitSlice";

const AddSupportTeamForm = ({ show, onClickNext,onFileDeleted, data = {} }) => {
  const dispatch = useDispatch();

  const [uploadHappened, setUploadHappened] = useState(false);  // NEW STATE

  const [state, setState] = useState({
    name: data?.name || "",
    email: data?.email || "",
    countryCode: data?.countryCode || countryData[0]?.dial_code || "",
    phoneNumber: data?.phoneNumber || "",
    fileId: data?.fileId || null,
    fileUrl: data?.fileUrl || "",
    fileUrlTemp: null,
    fileName: data?.fileName || ""
  });

  const [prevFileId, setPrevFileId] = useState(data?.fileId || null);
  const [validation, setValidation] = useState({});
  const { errorSingleMessage, setErrorSingleMessage, validateFile } = useFileValidation();

  const [prevFileData, setPrevFileData] = useState({
    fileId: data?.fileId || null,
    fileName: data?.fileName || null,
  });

  useEffect(() => {
    setState({
      name: data?.name || "",
      email: data?.email || "",
      countryCode: data?.countryCode || countryData[0]?.dial_code || "",
      phoneNumber: data?.phoneNumber || "",
      fileId: data?.fileId || null,
      fileUrl: data?.fileUrl || "",
      fileUrlTemp: null,
      fileName: data?.fileName || ""
    });
    setValidation({});
    setPrevFileId(data?.fileId || null);
    setPrevFileData({
      fileId: data?.fileId || null,
      fileName: data?.fileName || null,
    });
    setUploadHappened(false);  // Reset whenever data changes
  }, [data]);

  if (!show) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleConfirm = () => {
    const { errors, isValid } = SupportTeamFormValidations(state);
    setValidation(errors);

    if (isValid) {
      const payload = { ...state };
      if (!payload.fileId) delete payload.fileId;

      onClickNext(payload);

      setUploadHappened(false);  // Reset upload state on confirm

      dispatch(setSelectedFile(null));
      dispatch(previewImage(null));
    }
  };


  const handleImageCancel = () => {
    if (state?.fileId) {
      dispatch(fileDelete({ id: state.fileId }))
        .unwrap()
        .then(() => {
          setState(prev => ({
            ...prev,
            fileId: null,
            fileUrl: null,
            fileUrlTemp: null,
            fileName: null,
          }));
          setUploadHappened(false);

          dispatch(setSelectedFile(null));
          dispatch(previewImage(null));

          // Notify parent that file was deleted, but do NOT close card/modal
          if (onFileDeleted) {
            onFileDeleted({
              ...state,
              fileId: null,
              fileUrl: null,
              fileName: null,
            });
          }
        })
        .catch(() => setErrorSingleMessage("Failed to delete file."));
    }
  };
  const handleFileSelect = (event) => {
    const files = event.target.files;
    postFileData(files);
  };

  const handleDrag = (event) => event.preventDefault();

  const handleDrop = (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    postFileData(files);
  };

  function postFileData(files) {
    const valid = validateFile(files);
    if (!valid) return;

    const file = files[0];
    const previewUrl = URL.createObjectURL(file);

    if (prevFileData?.fileId) {
      dispatch(setPrePhotoFileId(prevFileId));
      setPrevFileId(prevFileId);
      dispatch(setPrevPhotoFileDetails(prevFileData));
      setPrevFileData({ fileId: null, fileName: null });
    }

    setState(prev => ({
      ...prev,
      fileUrlTemp: previewUrl,
      fileName: file.name,
    }));

    dispatch(setSelectedFile(file));

    dispatch(fileUpload(file))
      .unwrap()
      .then((res) => {
        const uploadedFile = res[0];
        setState(prev => ({
          ...prev,
          fileId: uploadedFile._id,
          fileUrl: previewUrl,  // or uploaded URL if available
          fileUrlTemp: null,
          fileName: uploadedFile.filename || prev.fileName,
        }));
        setPrevFileId(uploadedFile._id);

        setUploadHappened(true);  // IMPORTANT: mark upload happened
      })
      .catch(() => {
        setErrorSingleMessage("Failed to upload file");
        setState(prev => ({
          ...prev,
          fileUrlTemp: null,
        }));
      });
  }

  const handleCancel = () => {
    onClickNext(null);
  };

  return (
    <div className="modal" style={{ paddingBottom: "1.5rem" }}>
      <div className="form-scroll">
        <div className="form-flex">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="form-header">
              <div className="header">{data?.name ? "Edit Support Team" : "Add Support Team"}</div>
            </div>
            <div className="form-container">
              {/* Name */}
              <div className="form-label-input">
                <label className="form-label">
                  Name <Required />
                </label>
                <div className="form-input-container">
                  <input
                    name="name"
                    type="text"
                    value={state.name}
                    onChange={handleChange}
                    placeholder="Input"
                    className="form-input"
                    autoFocus
                  />
                </div>
                <ErrorMessageComponent message={validation.name} />
              </div>

              {/* Email */}
              <div className="form-label-input">
                <label className="form-label">
                  Email <Required />
                </label>
                <div className="form-input-container">
                  <input
                    name="email"
                    type="email"
                    value={state.email}
                    onChange={handleChange}
                    placeholder="Input"
                    className="form-input"
                  />
                </div>
                <ErrorMessageComponent message={validation.email} />
              </div>

              {/* Country Code */}
              <div className="form-label-input">
                <label className="form-label">Country Code</label>
                <div className="form-input-container">
                  <select
                    name="countryCode"
                    value={state.countryCode}
                    onChange={handleChange}
                    className="form-input"
                  >
                    {countryData.map((country) => (
                      <option key={country.code} value={country.dial_code}>
                        {country.name} {country.code}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Phone Number */}
              <div className="form-label-input">
                <label className="form-label">Phone Number</label>
                <div className="form-input-container">
                  <input
                    name="phoneNumber"
                    type="tel"
                    value={state.phoneNumber}
                    onChange={handleChange}
                    placeholder="Input"
                    className="form-input"
                  />
                </div>
                <ErrorMessageComponent message={validation.phoneNumber} />
              </div>

              {/* File Uploader */}
              <FileUploader
                previewShape="circular"
                headerName={SupportStringLabels.uploadPhoto}
                handleDragOver={handleDrag}
                handleDrop={handleDrop}
                handleFileSelect={handleFileSelect}
                type="supportTeamMember"
                accept="image/*"
              />

              {errorSingleMessage && (
                <p className="error image-error">{errorSingleMessage}</p>
              )}

              {/* Show preview ONLY if no upload happened yet */}
              {!uploadHappened && state?.fileUrl && (
                <ImagePreviewWithFilename
                  photoUrl={state.fileUrl}
                  fileName={state.fileName}
                  previewShape="circular"
                  handleCancel={handleImageCancel}
                />
              )}

              {/* Buttons */}
              <div
                className="support-team-buttons"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "1rem",
                  marginTop: "1rem",
                }}
              >
                <BlackButton onClickhandle={handleConfirm} title={data?.name ? "Update" : "Add"}>
                  {data?.name ? "Update" : "Add"}
                </BlackButton>

                <CancelButton onClickhandle={handleCancel} title="Cancel">
                  Cancel
                </CancelButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSupportTeamForm;